import React, { useState, useEffect } from 'react';
import { 
  Network, 
  Database, 
  Brain, 
  ChevronRight, 
  Menu, 
  X,
  Workflow,
  ShieldCheck,
  Cpu,
  Sparkles,
  UserCheck,
  Zap,
  LineChart,
  HardDrive,
  Wrench
} from 'lucide-react';
import BusinessShowcase from './components/BusinessShowcase';
import AIDiagnosis from './components/AIDiagnosis';

// --- 全新升级：动态变形图谱动画组件 ---
const DataFlywheelAnimation = () => {
  const [phase, setPhase] = useState(0);
  
  // 动画状态机：0(散乱) -> 1(网络) -> 2(大脑) -> 3(工具) -> 1(网络循环...)
  useEffect(() => {
    let timer;
    const runSequence = () => {
      setPhase((prev) => {
        if (prev === 0) return 1; // 2秒后从散乱进入网络
        if (prev === 1) return 2; // 3.5秒后组合成大脑
        if (prev === 2) return 3; // 3.5秒后组合成工具
        if (prev === 3) return 1; // 3.5秒后变回网络（开启闭环）
        return 1;
      });
    };

    // 初始状态停留较短，之后循环状态停留较长
    const delay = phase === 0 ? 2000 : 3500;
    timer = setTimeout(runSequence, delay);
    return () => clearTimeout(timer);
  }, [phase]);

  // 生成 16 个节点的坐标
  const nodesCount = 16;
  
  // 状态 0: 凌乱的固定资产数据
  const coordsScattered = [
    [-140, -120], [150, -100], [-130, 90], [130, 140],
    [-70, 160], [80, -150], [-160, -10], [160, 20],
    [-50, -70], [60, 80], [-90, 50], [100, -60],
    [-20, -160], [30, 170], [-150, 60], [110, -110]
  ];

  // 状态 1: 有序图谱 (正圆形)
  const coordsGraph = Array.from({ length: nodesCount }).map((_, i) => {
    const angle = (i * Math.PI * 2) / nodesCount;
    const r = 140;
    return [Math.cos(angle) * r, Math.sin(angle) * r];
  });

  // 状态 2: 大脑形状 (左右半脑与脑干轮廓)
  const coordsBrain = [
    [-30, -110], [30, -110], // 顶部
    [-80, -80], [80, -80],   // 侧面上部
    [-110, -20], [110, -20], // 侧面中部 (最宽处)
    [-100, 50], [100, 50],   // 侧面下部
    [-50, 100], [50, 100],   // 底部脑叶
    [-40, -10], [40, -10],   // 内部纹理上
    [-40, 40], [40, 40],     // 内部纹理下
    [0, -60], [0, 60]        // 脑干中轴线
  ];

  // 状态 3: 工具形状 (扳手 - 象征落地工具)
  const coordsTool = [
    [-40, -110], [0, -130], [40, -110], // 扳手头部外圈
    [-30, -60], [0, -50], [30, -60],    // 扳手头部内圈
    [-15, -10], [15, -10],              // 握柄上段
    [-15, 40], [15, 40],                // 握柄中段
    [-15, 90], [15, 90],                // 握柄下段
    [-30, 130], [0, 140], [30, 130],    // 扳手尾部
    [0, 40]                             // 握柄中心轴
  ];

  // 根据当前状态获取坐标集
  const getCurrentCoords = () => {
    if (phase === 0) return coordsScattered;
    if (phase === 1) return coordsGraph;
    if (phase === 2) return coordsBrain;
    if (phase === 3) return coordsTool;
    return coordsGraph;
  };

  const activeCoords = getCurrentCoords();

  return (
    <div className="relative w-full h-[400px] lg:h-[500px] flex items-center justify-center scale-90 lg:scale-100 select-none pointer-events-none">
      
      {/* 动态光晕背景 (随着状态切换颜色) */}
      <div className={`absolute inset-0 m-auto w-80 h-80 blur-[80px] rounded-full transition-all duration-1000 ease-in-out
        ${phase === 0 ? 'bg-slate-800/20' : 
          phase === 1 ? 'bg-blue-600/30' : 
          phase === 2 ? 'bg-purple-600/30' : 
          'bg-cyan-600/30'}`}
      ></div>

      {/* 动态连线 SVG 层：线条会跟随着节点平滑移动 */}
      <svg className="absolute inset-0 w-full h-full overflow-visible z-10" style={{ left: '50%', top: '50%' }}>
        {/* 外圈/轮廓连线 */}
        {activeCoords.map((_, i) => {
          const currentPos = activeCoords[i];
          const nextPos = activeCoords[(i + 1) % nodesCount];
          return (
            <line 
              key={`outline-${i}`}
              x1={currentPos[0]} y1={currentPos[1]} 
              x2={nextPos[0]} y2={nextPos[1]}
              stroke="currentColor" strokeWidth="2"
              className={`transition-all duration-1000 ease-in-out
                ${phase === 0 ? 'text-slate-700/0' : // 散乱状态不显示连线
                  phase === 1 ? 'text-blue-500/40' : 
                  phase === 2 ? 'text-purple-500/50' : 
                  'text-cyan-500/50'}`}
            />
          );
        })}
        {/* 内部交叉连线 (增加科技感与复杂网状感) */}
        {activeCoords.map((_, i) => {
          if (i >= 8) return null; // 画对角线/交叉线
          const currentPos = activeCoords[i];
          const nextPos = activeCoords[i + 8];
          return (
            <line 
              key={`cross-${i}`}
              x1={currentPos[0]} y1={currentPos[1]} 
              x2={nextPos[0]} y2={nextPos[1]}
              stroke="currentColor" strokeWidth="1" strokeDasharray="4 4"
              className={`transition-all duration-1000 ease-in-out
                ${phase === 0 ? 'text-slate-700/0' : 
                  phase === 1 ? 'text-indigo-400/20' : 
                  phase === 2 ? 'text-pink-400/20' : 
                  'text-teal-400/20'}`}
            />
          );
        })}
      </svg>

      {/* 游离的数据节点 */}
      <div className="absolute top-1/2 left-1/2 z-20">
        {activeCoords.map((pos, i) => (
          <div 
            key={`node-${i}`}
            className={`absolute w-10 h-10 -ml-5 -mt-5 flex items-center justify-center rounded-xl transition-all duration-1000 ease-in-out transform
              ${phase === 0 
                ? 'bg-[#0f1525] border border-slate-700 opacity-60' 
                : 'bg-[#0b101d]/80 border backdrop-blur-md animate-pulse'} // 激活后不断闪亮
              ${phase === 1 ? 'border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.3)]' : ''}
              ${phase === 2 ? 'border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.3)]' : ''}
              ${phase === 3 ? 'border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.3)]' : ''}
            `}
            style={{
              transform: `translate(${pos[0]}px, ${pos[1]}px)`,
              animationDelay: `${i * 150}ms` // 让每个节点的闪亮(pulse)错开，形成呼吸感
            }}
          >
            <Database className={`w-4 h-4 transition-colors duration-1000
              ${phase === 0 ? 'text-slate-600' : 
                phase === 1 ? 'text-blue-300' : 
                phase === 2 ? 'text-purple-300' : 
                'text-cyan-300'}`} 
            />
          </div>
        ))}
      </div>

      {/* 中心枢纽标识符 */}
      <div className={`absolute z-30 flex items-center justify-center overflow-hidden transition-all duration-1000 ease-in-out
        ${phase === 0 
          ? 'w-24 h-24 bg-[#0b101d] border border-slate-700 rounded-2xl' 
          : 'w-28 h-28 rounded-full border-2'
        }
        ${phase === 1 ? 'bg-gradient-to-br from-indigo-900 to-blue-900 border-blue-400 shadow-[0_0_50px_rgba(59,130,246,0.5)]' : ''}
        ${phase === 2 ? 'bg-gradient-to-br from-purple-900 to-pink-900 border-pink-400 shadow-[0_0_50px_rgba(236,72,153,0.5)]' : ''}
        ${phase === 3 ? 'bg-gradient-to-br from-cyan-900 to-teal-900 border-cyan-400 shadow-[0_0_50px_rgba(6,182,212,0.5)]' : ''}
        `}
      >
        {/* 图标平滑切换 */}
        <div className="relative w-full h-full flex flex-col items-center justify-center">
          <div className={`absolute flex flex-col items-center transition-all duration-700 ${phase === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <Database className="w-8 h-8 text-slate-500 mb-1" />
            <span className="text-[10px] text-slate-500 font-bold tracking-widest">固定资产</span>
          </div>
          
          <div className={`absolute flex flex-col items-center transition-all duration-700 ${phase === 1 ? 'opacity-100 scale-100 delay-300' : 'opacity-0 scale-50'}`}>
            <Network className="w-10 h-10 text-blue-200 mb-1 drop-shadow-lg" />
            <span className="text-[10px] text-blue-100 font-bold tracking-widest">情景图谱</span>
          </div>

          <div className={`absolute flex flex-col items-center transition-all duration-700 ${phase === 2 ? 'opacity-100 scale-100 delay-300' : 'opacity-0 scale-50'}`}>
            <Brain className="w-10 h-10 text-pink-200 mb-1 drop-shadow-lg" />
            <span className="text-[10px] text-pink-100 font-bold tracking-widest">企业大脑</span>
          </div>

          <div className={`absolute flex flex-col items-center transition-all duration-700 ${phase === 3 ? 'opacity-100 scale-100 delay-300' : 'opacity-0 scale-50'}`}>
            <Wrench className="w-10 h-10 text-cyan-200 mb-1 drop-shadow-lg" />
            <span className="text-[10px] text-cyan-100 font-bold tracking-widest">智能工具</span>
          </div>
        </div>
      </div>
    </div>
  );
};
// --- 动画组件结束 ---


export default function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aiDiagnosisOpen, setAiDiagnosisOpen] = useState(false);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: '首页', href: '#home' },
    { name: '价值重塑', href: '#value' },
    { name: '引擎架构', href: '#engine' },
    { name: '产业底座', href: '#empowerment' },
    { name: '成功案例', href: '#cases' },
  ];

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-50 font-sans selection:bg-indigo-500 selection:text-white">
      
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-[#070b14]/90 backdrop-blur-md shadow-2xl py-3 border-b border-indigo-900/30' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 group cursor-pointer">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-600 flex items-center justify-center shadow-[0_0_20px_rgba(79,70,229,0.4)] group-hover:shadow-[0_0_30px_rgba(79,70,229,0.6)] transition-all">
                <Network className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-blue-300">
                中海服
              </span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex space-x-8 items-center">
              {navLinks.map((link) => (
                <a 
                  key={link.name} 
                  href={link.href} 
                  className="text-sm font-medium text-slate-300 hover:text-indigo-400 transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <button 
                onClick={() => setAiDiagnosisOpen(true)}
                className="text-sm font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 text-white hover:from-emerald-500 hover:to-cyan-500 px-6 py-2.5 rounded-full transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)]"
              >
                免费AI诊断
              </button>
              <a href="#contact" className="text-sm font-bold bg-white text-indigo-900 hover:bg-indigo-50 px-6 py-2.5 rounded-full transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                探讨合作
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-white"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0f1c] border-t border-slate-800 absolute w-full left-0 mt-3">
            <div className="px-4 pt-2 pb-4 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-3 text-base font-medium text-slate-300 hover:text-indigo-400 hover:bg-slate-800/50 rounded-md"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section (包含最新变形网络动画) */}
      <section id="home" className="relative min-h-screen flex items-center pt-24 pb-12 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[#070b14] opacity-85 z-10"></div>
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')] bg-cover bg-center mix-blend-luminosity opacity-20"></div>
        </div>
        
        <div className="absolute top-1/2 left-0 w-[40rem] h-[40rem] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-600/10 via-blue-900/5 to-transparent rounded-full blur-[100px] z-10"></div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            
            {/* 左侧：核心文案 */}
            <div className="flex-1 text-center lg:text-left pt-10 lg:pt-0">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-900/20 border border-indigo-500/30 backdrop-blur-md mb-8 animate-fade-in-up">
                <Sparkles className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-medium text-indigo-200 tracking-widest">中海服：让企业留存的数据活起来</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight mb-8 leading-[1.1]">
                <span className="block text-white opacity-90">唤醒沉睡资产</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-blue-400 to-cyan-400 mt-2">
                  驱动业务飞轮
                </span>
              </h1>
              
              <p className="mt-8 max-w-2xl mx-auto lg:mx-0 text-lg md:text-xl text-slate-400 mb-10 font-light leading-relaxed">
                员工会离职，但业务能力永驻。<br className="hidden md:block"/>
                我们将散乱的固化数据提炼为有序的<strong className="text-blue-300 font-medium">情景图谱</strong>，<br className="hidden md:block"/>
                组合成企业级 AI 大脑与提效工具，实现资产持续增值。
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-5">
                <a href="#value" className="inline-flex justify-center items-center px-8 py-4 text-lg font-bold rounded-full text-[#070b14] bg-white hover:bg-slate-200 shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all">
                  探索核心价值
                </a>
                <a href="#engine" className="inline-flex justify-center items-center gap-2 px-8 py-4 text-lg font-medium rounded-full text-indigo-300 bg-indigo-900/20 border border-indigo-500/30 hover:bg-indigo-900/40 backdrop-blur-sm transition-all">
                  查看底层架构 <ChevronRight className="w-5 h-5" />
                </a>
                <button 
                  onClick={() => setAiDiagnosisOpen(true)}
                  className="inline-flex justify-center items-center gap-2 px-8 py-4 text-lg font-bold rounded-full text-white bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all"
                >
                  <Sparkles className="w-5 h-5" />
                  免费AI诊断
                </button>
              </div>
            </div>

            {/* 右侧：动态图谱/大脑/工具变形动画 */}
            <div className="flex-1 w-full max-w-lg lg:max-w-none mt-12 lg:mt-0">
              <DataFlywheelAnimation />
            </div>

          </div>
        </div>
      </section>

      {/* Value Proposition Framework */}
      <section id="value" className="py-32 bg-[#070b14] relative border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">重塑数据生命周期的三大支柱</h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            {/* Pillar 1 */}
            <div className="bg-[#0b101d] border border-slate-800/80 rounded-3xl p-10 relative overflow-hidden group hover:border-blue-500/50 transition-colors duration-500">
              <div className="w-16 h-16 rounded-2xl bg-blue-900/30 flex items-center justify-center mb-8 border border-blue-700/50">
                <HardDrive className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">流程数据激活</h3>
              <p className="text-slate-400 leading-relaxed">
                企业服务器里沉睡的日志、表单和轨迹，不再是昂贵的存储垃圾。我们深度清洗、激活这些历史过程数据，将其提炼为蕴含企业专属 Know-How 的结构化资产，让“死数据”重新焕发活力。
              </p>
            </div>

            {/* Pillar 2 */}
            <div className="bg-[#0b101d] border border-indigo-900/50 rounded-3xl p-10 relative overflow-hidden group shadow-[0_0_30px_rgba(79,70,229,0.05)] hover:border-indigo-500/50 transition-colors duration-500">
              <div className="w-16 h-16 rounded-2xl bg-indigo-900/50 flex items-center justify-center mb-8 border border-indigo-600/50">
                <Brain className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">全面业务智能化</h3>
              <p className="text-slate-300 leading-relaxed">
                将激活后的数据资产构建为逻辑严密的“情景图谱”。系统不仅能记录过去，更能结合当前业务情境主动推理、预测风险，辅助业务人员做出最优决策，实现从自动化到智能化的跨越。
              </p>
            </div>

            {/* Pillar 3 */}
            <div className="bg-[#0b101d] border border-cyan-900/50 rounded-3xl p-10 relative overflow-hidden group hover:border-cyan-500/50 transition-colors duration-500">
              <div className="w-16 h-16 rounded-2xl bg-cyan-900/30 flex items-center justify-center mb-8 border border-cyan-700/50">
                <ShieldCheck className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">极致安全与性能保障</h3>
              <p className="text-slate-400 leading-relaxed">
                摒弃昂贵且存在泄密风险的公有大模型。我们为企业私有化部署微调专属“小模型（SLM）”。数据绝不出域，既捍卫了绝对的商业机密，又大幅降低了算力成本与推理延迟。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Core Engine: Asset -> Graph -> Small Model */}
      <section id="engine" className="py-28 bg-[#0a0f1c] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-indigo-900 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            
            {/* Left: Visualization of the 3-Step Transformation */}
            <div className="lg:col-span-5 relative order-2 lg:order-1">
              <div className="w-full aspect-[4/5] bg-[#070b14] rounded-[2.5rem] border border-slate-800/80 p-8 flex flex-col justify-between relative overflow-hidden shadow-2xl">
                {/* Visual Depth */}
                <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 via-[#070b14] to-indigo-900/10"></div>
                
                {/* Step 1: Enterprise Data Assets */}
                <div className="relative z-10 flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-3">
                    <Database className="w-6 h-6 text-slate-400" />
                  </div>
                  <span className="text-sm font-bold text-slate-300 tracking-widest">企业数据资产</span>
                  <div className="text-xs text-slate-500 mt-1">海量留存过程流水</div>
                </div>

                {/* Arrow Down */}
                <div className="relative z-10 flex justify-center py-2">
                  <div className="w-px h-10 bg-gradient-to-b from-slate-600 to-blue-500"></div>
                </div>

                {/* Step 2: Scenario Graph */}
                <div className="relative z-10 flex flex-col items-center">
                  <div className="w-16 h-16 rounded-xl bg-blue-900/30 border border-blue-500/50 flex items-center justify-center mb-3 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                    <Network className="w-8 h-8 text-blue-400" />
                  </div>
                  <span className="text-sm font-bold text-blue-400 tracking-widest">企业情景图谱</span>
                  <div className="text-xs text-blue-500/70 mt-1">因果关联与知识重塑</div>
                </div>

                {/* Arrow Down */}
                <div className="relative z-10 flex justify-center py-2">
                  <div className="w-px h-10 bg-gradient-to-b from-blue-500 to-indigo-500"></div>
                </div>

                {/* Step 3: Fine-tuned Small Model */}
                <div className="relative z-10 flex flex-col items-center">
                  <div className="absolute bottom-10 bg-indigo-600/20 blur-2xl rounded-full w-32 h-32"></div>
                  <div className="w-20 h-20 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(79,70,229,0.4)] border border-indigo-400/50 relative z-10">
                    <Cpu className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-base font-black text-white tracking-widest mt-4 uppercase relative z-10">微调专属小模型</span>
                </div>
              </div>
            </div>

            {/* Right: Narrative & Business Value */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-8 leading-[1.2]">
                铁打的数字营盘，<br/>
                持续增值的企业大脑。
              </h2>
              <p className="text-xl text-slate-400 mb-12 font-light leading-relaxed">
                企业最大的浪费，是任由高薪员工积累的业务经验随其离职而流失。中海服通过“资产提取 ➔ 图谱构建 ➔ 模型微调”的三级跳引擎，彻底扭转这一局面。
              </p>

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-12">
                {/* Feature 1 */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-900/50 flex items-center justify-center border border-emerald-500/50">
                      <UserCheck className="w-5 h-5 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">员工离职，能力永续留存</h3>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed pl-13">
                    老员工的隐性知识不再是带得走的个人财富。我们将他们在系统中留存的决策过程转化为情景图谱，固化为企业永不离职的数字资产。
                  </p>
                </div>

                {/* Feature 2 */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-900/50 flex items-center justify-center border border-blue-500/50">
                      <LineChart className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">沉睡记录变为增值资产</h3>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed pl-13">
                    打破“存而不用”的魔咒。将沉睡的流程流水提炼为黄金语料，数据真正成为资产，源源不断地为后续业务运营与推理预测创造增量价值。
                  </p>
                </div>

                {/* Feature 3 */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-900/50 flex items-center justify-center border border-indigo-500/50">
                      <Zap className="w-5 h-5 text-indigo-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">自学习的业务飞轮</h3>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed pl-13">
                    利用结构化图谱不断微调（Fine-tune）专属小模型。<strong className="text-indigo-300 font-semibold">业务越流转，图谱越丰富；图谱越丰富，模型越聪明</strong>，构建自我进化的智能闭环。
                  </p>
                </div>
                
                {/* Feature 4: Security & Cost */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-900/50 flex items-center justify-center border border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                      <ShieldCheck className="w-5 h-5 text-cyan-400" />
                    </div>
                    <h3 className="text-xl font-bold text-cyan-300">专属微调小模型 (SLM)</h3>
                  </div>
                  <p className="text-cyan-100/70 text-sm leading-relaxed pl-13">
                    专为企业场景打造。摒弃通用大模型的臃肿、高算力成本与数据泄露风险。私有化部署的小模型具备更高的垂直领域准度、更快的响应与绝对的物理隔离安全。
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Empowerment Subsidiary Context */}
      <section id="empowerment" className="py-24 bg-gradient-to-b from-[#0a0f1c] to-[#070b14] border-t border-slate-800/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Workflow className="w-16 h-16 text-blue-500 mx-auto mb-8 opacity-80" />
          <h2 className="text-4xl font-bold text-white mb-8">深入产业腹地的智能底座</h2>
          <p className="text-xl text-slate-400 mb-12 leading-relaxed font-light max-w-4xl mx-auto">
            中海服作为底层的科技引擎，正将“数据资产 ➔ 情景图谱 ➔ 微调模型”的能力源源不断地向下游沉淀。
            我们在交通与能源等垂直领域孵化了如 <span className="text-cyan-400 font-bold mx-2 border-b border-cyan-400/50">天枢智网</span> 等行业领军应用，
            真正实现了将企业隐性经验转化为百亿级产业价值。
          </p>
          <button className="bg-transparent border border-slate-600 text-slate-300 hover:bg-white hover:text-[#070b14] hover:border-white px-10 py-4 rounded-full font-bold transition-all text-lg shadow-[0_0_0_rgba(255,255,255,0)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]">
            了解天枢智网产业案例
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#03050a] py-16 border-t border-slate-900/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <Network className="w-8 h-8 text-indigo-600" />
                <span className="text-2xl font-bold text-white tracking-widest">中海服</span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light">
                中海服信息科技股份有限公司<br/>
                让企业留存数据活起来，将数字能力永久留存。<br/>做产业数字化转型的顶级人工智能底座。
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-6 tracking-widest text-sm uppercase">引擎核心路径</h4>
              <ul className="space-y-4 text-sm text-slate-500 font-light">
                <li className="hover:text-indigo-400 cursor-pointer transition-colors flex items-center gap-2"><div className="w-1 h-1 rounded-full bg-indigo-600"></div>流程过程数据激活平台</li>
                <li className="hover:text-indigo-400 cursor-pointer transition-colors flex items-center gap-2"><div className="w-1 h-1 rounded-full bg-indigo-600"></div>认知情景图谱生成网络</li>
                <li className="hover:text-indigo-400 cursor-pointer transition-colors flex items-center gap-2"><div className="w-1 h-1 rounded-full bg-indigo-600"></div>行业专属小模型微调服务</li>
                <li className="hover:text-indigo-400 cursor-pointer transition-colors flex items-center gap-2"><div className="w-1 h-1 rounded-full bg-indigo-600"></div>私有化高性能安全部署</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-6 tracking-widest text-sm uppercase">联系方式</h4>
              <ul className="space-y-4 text-sm text-slate-500 font-light">
                <li>商务合作：business@zhonghaifu.com</li>
                <li>技术生态：ai-lab@zhonghaifu.com</li>
                <li>中国总部</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-700 text-sm">
              &copy; 2026 中海服信息科技股份有限公司. 保留所有权利.
            </p>
            <div className="flex gap-8 text-sm text-slate-600">
              <span className="hover:text-slate-400 cursor-pointer transition-colors">隐私政策</span>
              <span className="hover:text-slate-400 cursor-pointer transition-colors">服务协议</span>
            </div>
          </div>
        </div>
      </footer>

      {/* 业务分层展示 */}
      <BusinessShowcase />

      {/* AI诊断弹窗 */}
      <AIDiagnosis isOpen={aiDiagnosisOpen} onClose={() => setAiDiagnosisOpen(false)} />
    </div>
  );
}